import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import { contentAPI } from "../services/api";
import { handleAnchorClick, getCurrentSection } from "../utils/smoothScroll";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoData, setLogoData] = useState({ logo_url: null, logo_red_url: null });
  const [activeSection, setActiveSection] = useState('hero');
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section for navigation highlighting (order must match DOM order on page)
      const sections = ['hero', 'services', 'about', 'qualitaet', 'kontakt'];
      const current = getCurrentSection(sections, 150);
      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Fetch logo data from API
    const fetchLogoData = async () => {
      try {
        const sections = await contentAPI.getSections("settings").catch(() => []);
        const logoSection = sections.find(s => s.section_type === "logo");
        if (logoSection?.content_data) {
          setLogoData({
            logo_url: logoSection.content_data.logo_url || null,
            logo_red_url: logoSection.content_data.logo_red_url || null,
          });
        }
      } catch (error) {
        console.error("Error fetching logo data:", error);
      }
    };
    fetchLogoData();
  }, []);

  const navItems = [
    { label: t("nav.home"), anchor: "hero" },
    { label: t("nav.services"), anchor: "services" },
    { label: t("nav.about"), anchor: "about" },
    { label: t("nav.qualitaet"), anchor: "qualitaet" },
    { label: t("nav.contact"), anchor: "kontakt" }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-lg border-b-2 border-[var(--vortex-border)]"
          : "bg-white"
      }`}
    >
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[var(--vortex-accent-primary)] focus:text-[#6a5959] focus:rounded focus:font-semibold"
      >
        Zum Hauptinhalt springen
      </a>
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <nav className="flex items-center justify-between" role="navigation" aria-label="Hauptnavigation">
          {/* Logo */}
          <Logo 
            size="default" 
            logoImage={logoData.logo_url}
            logoImageRed={logoData.logo_red_url}
          />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) => (
              <a
                key={item.anchor}
                href={`#${item.anchor}`}
                onClick={(e) => {
                  handleAnchorClick(e, item.anchor);
                  setActiveSection(item.anchor);
                  setIsMobileMenuOpen(false);
                }}
                className={`text-lg font-medium min-h-[44px] min-w-[44px] flex items-center px-3 transition-colors duration-200 relative ${
                  activeSection === item.anchor
                    ? 'text-[var(--vortex-accent-primary)]'
                    : 'text-[var(--vortex-text-primary)] hover:text-[var(--vortex-accent-primary)]'
                }`}
                aria-current={activeSection === item.anchor ? 'page' : undefined}
              >
                {item.label}
                {activeSection === item.anchor && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--vortex-accent-primary)]"></span>
                )}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <a
              href="#kontakt"
              onClick={(e) => handleAnchorClick(e, 'kontakt')}
              className="btn-primary px-6 py-3 text-base font-semibold"
            >
              {t("nav.getProposal")}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-[var(--vortex-text-primary)] p-3 rounded-lg hover:bg-[var(--vortex-surface)] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 bg-[var(--vortex-surface-alt)] rounded-lg p-6 border-2 border-[var(--vortex-border)] shadow-lg">
            <div className="flex flex-col space-y-3">
              {navItems.map((item) => (
                <a
                  key={item.anchor}
                  href={`#${item.anchor}`}
                  onClick={(e) => {
                    handleAnchorClick(e, item.anchor);
                    setActiveSection(item.anchor);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-lg font-medium py-3 px-4 rounded-lg transition-colors min-h-[44px] flex items-center ${
                    activeSection === item.anchor
                      ? 'text-[var(--vortex-accent-primary)] bg-[var(--vortex-surface)]'
                      : 'text-[var(--vortex-text-primary)] hover:bg-[var(--vortex-surface)]'
                  }`}
                  aria-current={activeSection === item.anchor ? 'page' : undefined}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#kontakt"
                onClick={(e) => {
                  handleAnchorClick(e, 'kontakt');
                  setIsMobileMenuOpen(false);
                }}
                className="btn-primary w-full py-3 text-base font-semibold text-center mt-2"
              >
                {t("nav.getProposal")}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;